import React, { useContext, useEffect, useState } from "react";
import {
    Button,
    LottieLoadingIndicator,
    StandardModalDescription,
    StandardModalActionGroup,
    StandardModal,
    StandardModalHeading,
    Toast
} from "src/components";
import { ManageCategoriesTable } from "./ManageCategoriesTable";
import { CategoryEditor } from "./CategoryEditor";
import { BodyAndMindContext } from "../../BodyAndMindContext";
import { reOrderCategory } from "../../api";
import { LoadingIndicatorButton } from "src/scenes/Survey/components/Utils";
import { capitalizeString, customNames } from "src/utils/helpers";
import pluralize from "pluralize";

function ManageCategories() {
    const {
        featureName,
        categoriesList,
        contentsList,
        contentListHasFetched,
        showAddCategory,
        toggleAddCategory,
        showEditCategory,
        toggleEditCategory,
        toggleManageCategory
    } = useContext(BodyAndMindContext);
    const [isLoading, setIsLoading] = useState(false);
    const [payload, setPayload] = useState({});
    const [hiddenCategoryUuids, setHiddenCategoryUuids] = useState([]);
    const noBodyAndMindContent = contentListHasFetched && contentsList.length === 0;
    const allCategoriesAreHidden =
        categoriesList.length > 0 &&
        categoriesList.every(category => hiddenCategoryUuids.includes(category.uuid));
    const shouldShowContentVisibilityModal =
        noBodyAndMindContent &&
        allCategoriesAreHidden;
    const [showContentVisibilityModal, setShowContentVisibilityModal] = useState(shouldShowContentVisibilityModal);

    const [selectedCategory, setSelectedCategory] = useState({});
    const employeeGroupName = capitalizeString(pluralize(customNames.employees));
    const clientGroupName = capitalizeString(pluralize(customNames.clients));

    useEffect(() => {
        setHiddenCategoryUuids(
            categoriesList
                .filter(category => category.deletedAt !== null)
                .map(category => category.uuid)
        );
    }, [categoriesList]);

    useEffect(() => {
        if (shouldShowContentVisibilityModal) {
            setShowContentVisibilityModal(true);
        } else {
            setShowContentVisibilityModal(false);
        }
    }, [shouldShowContentVisibilityModal]);

    const reOrderHandler = async () => {
        setIsLoading(true);
        try {
            const response = await reOrderCategory({ "categories": payload });
            if (response) {
                setPayload({});
                Toast.success("Categories reordered successfully");
                toggleManageCategory();
            }
        } catch (error) {
            Toast.error(error.getApiErrorDetails().message);
            console.log("Error:", error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <StandardModal
                open={true}
                onClose={toggleManageCategory}
                title="Manage Categories"
                description={`Organise your ${featureName} content by managing existing categories, creating your own, and choosing which Me Business categories to display.`}
                widthMode="table"
                footer={(
                    <StandardModalActionGroup $pushRight>
                        <Button
                            disabled={isLoading}
                            onClick={() => toggleManageCategory()}
                            border="1px solid #000"
                            color="#000"
                            width="140px"
                            height="38px"
                            style={{ borderRadius: "7px", boxShadow: "none" }}
                        >
                            Close
                        </Button>
                        {isLoading ?
                            <LoadingIndicatorButton>
                                <LottieLoadingIndicator height="2rem" width="2rem" margin="5px" containerHeight="2rem" />
                            </LoadingIndicatorButton>
                            :
                            <Button
                                onClick={Object.keys(payload).length === 0 ? () => toggleManageCategory() : () => reOrderHandler()}
                                backgroundColor="#171A22"
                                border="1px solid #171A22"
                                color="#fff"
                                width="140px"
                                height="38px"
                                style={{ borderRadius: "7px", boxShadow: "none" }}
                            >
                                Save
                            </Button>}
                    </StandardModalActionGroup>
                )}
            >
                <ManageCategoriesTable
                    data={categoriesList || []}
                    setPayload={setPayload}
                    manageCategory={{ setSelectedCategory, toggleEditCategory }}
                    setHiddenCategoryUuids={setHiddenCategoryUuids}
                />
            </StandardModal>
            {showAddCategory && (
                <CategoryEditor open={showAddCategory} onClose={toggleAddCategory} featureName={featureName} />
            )}

            {showEditCategory && (
                <CategoryEditor open={showEditCategory} onClose={toggleEditCategory} category={selectedCategory} featureName={featureName} />
            )}
            {showContentVisibilityModal && (
                <StandardModal
                    open={true}
                    onClose={() => setShowContentVisibilityModal(false)}
                    hideCloseButton={true}
                    widthMode="medium"
                    bodyPadding="2rem 2.6rem 2.4rem"
                    bodyGap="2rem"
                    footer={(
                        <StandardModalActionGroup $center>
                            <Button
                                onClick={() => setShowContentVisibilityModal(false)}
                                border="1px solid #000"
                                color="#000"
                                width="140px"
                                height="38px"
                                style={{ borderRadius: "7px", boxShadow: "none" }}
                            >
                                Close
                            </Button>
                        </StandardModalActionGroup>
                    )}
                >
                    <StandardModalHeading align="center" size="2rem">
                        Body & Mind May Appear Empty
                    </StandardModalHeading>
                    <StandardModalDescription align="center" maxWidth="100%">
                        You can hide all Me content. However, if you have not published any Body & Mind <br />
                        content for {employeeGroupName} or {clientGroupName}, they will not see anything in the Body & Mind <br />
                        feature.
                    </StandardModalDescription>
                </StandardModal>
            )}
        </>
    );
}

export default ManageCategories;
